import { useMemo, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput, TdSelect } from '../../../../components/inline-add-table/cells';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { useRecruitmentUsers } from '../../vacancies/useRecruitmentUsers';
import { attritionCategoriesApi } from '../attritionCategoriesApi';
import type { AttritionCategoryDetail } from '../attritionCategoryTypes';
import { sampleAttritionCategory } from '../utils/sampleAttritionCategory';

const ID_PREFIX = 'attrition-category';

/** `reviewerUserId` to `reviewer-user-id` — exactly as `focusValidationError` does it. */
const kebab = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * The id of one inline cell — `attrition-category-documents.2.reviewer-user-id`. A CONTRACT with
 * the server, which names a rejected line `documents.2.reviewerUserId`.
 */
const cellId = (index: number, field: string) => `${ID_PREFIX}-documents.${index}.${kebab(field)}`;

interface DraftLine {
  key: string;
  /** The line's own id when it is already on the checklist — what keeps the filed requests pointing at it. */
  id: string | null;
  name: string;
  reviewerUserId: string | null;
  description: string;
}

const EMPTY = (): DraftLine => ({ key: uid(), id: null, name: '', reviewerUserId: null, description: '' });

/** A row nobody touched — the server's `IsBlank`. */
const isBlank = (line: DraftLine) => line.name.trim() === '' && line.reviewerUserId == null && line.description.trim() === '';

/** A row somebody finished: the document named, and somebody to review it. */
const isFilled = (line: DraftLine) => line.name.trim() !== '' && line.reviewerUserId != null;

/**
 * Create or edit one attrition category and its checklist, in one save.
 *
 * **The checklist is LINES, each naming the account that reviews that document** (product call,
 * 29 Oct 2026) — the clearance goes to one office, the medical certificate to another. A line
 * already on the checklist keeps its id, so the requests filed against it keep pointing at it;
 * one removed here is taken off for new requests and stays on the ones already filed.
 *
 * **The reviewer picker is this module's own door** (`attrition-categories/user-options`), so an
 * administrator maintaining the checklist needs no Users key to see whom to name.
 */
export function AttritionCategoryModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: AttritionCategoryDetail | null;
  onClose: () => void;
  onSaved: (saved: AttritionCategoryDetail) => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [lines, setLines] = useState<DraftLine[]>(() =>
    editing != null && editing.documents.length > 0
      ? editing.documents.map((d) => ({
          key: uid(),
          id: d.id,
          name: d.name,
          reviewerUserId: d.reviewerUserId,
          description: d.description ?? '',
        }))
      : [EMPTY()],
  );

  const { users, loading: loadingUsers, error: usersError } = useRecruitmentUsers(attritionCategoriesApi.userOptions);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    THE CURRENT REVIEWER STAYS OFFERED on an edit — the feed carries live accounts only, so a
    reviewer since deactivated would otherwise leave the cell holding an id it cannot show, and
    the reader would not see why the line is waiting on nobody.
  */
  const reviewerOptions = useMemo(() => {
    const live = users.map((u) => ({ value: u.userId, label: u.displayName }));
    const kept = (editing?.documents ?? [])
      .filter((d) => !live.some((o) => o.value === d.reviewerUserId))
      .map((d) => ({ value: d.reviewerUserId, label: d.reviewerName ?? 'Current reviewer', sublabel: 'Account deactivated' }));

    return [...live, ...kept.filter((o, i) => kept.findIndex((k) => k.value === o.value) === i)];
  }, [users, editing]);

  const filledCount = lines.filter(isFilled).length;
  const linesValid = filledCount > 0 && lines.every((l) => isFilled(l) || isBlank(l));
  const valid = name.trim().length > 0 && linesValid;
  const canSave = valid && confirmed && !saving;

  const saveDisabledReason = saving
    ? null
    : name.trim() === ''
      ? 'Name the category.'
      : filledCount === 0
        ? 'Add at least one document, with the account that reviews it.'
        : !linesValid
          ? 'Every document needs a name and a reviewer. Remove the unfinished one, or fill it in.'
          : !confirmed
            ? 'Tick the confirm box to submit.'
            : null;

  function patch(key: string, change: Partial<DraftLine>) {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...change } : line)));
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
      // EVERY row is sent, blanks included, so a rejection's index is the row on screen.
      documents: lines.map((l) => ({
        id: l.id,
        name: l.name.trim(),
        description: l.description.trim() || null,
        reviewerUserId: l.reviewerUserId,
      })),
    };

    try {
      const saved = editing
        ? await attritionCategoriesApi.update(editing.id, request)
        : await attritionCategoriesApi.create(request);

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this category.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: ID_PREFIX,
        // The refusals naming the WHOLE checklist — none at all, the cap — are answered at the `+`.
        fieldIds: { documents: `${ID_PREFIX}-add-document` },
      });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleAttritionCategory(users);

    if (editing === null) setName(sample.name);
    setDescription(sample.description);
    setLines(
      sample.documents.map((d) => ({
        key: uid(),
        id: null,
        name: d.name,
        reviewerUserId: d.reviewerUserId,
        description: d.description,
      })),
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.name}` : 'New category'}
      size="2xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || loadingUsers }}
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {usersError ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">
              The accounts could not be loaded, so no reviewer can be chosen. Close this form and open it again.
            </p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id={`${ID_PREFIX}-name`}
            label="Name"
            required
            description="The kind of separation — Retirement, Resignation, Illness."
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() ? error?.fieldError('name') : 'Name the category.'}
            disabled={saving}
          />

          <TextField
            id={`${ID_PREFIX}-description`}
            label="Description"
            description="One line saying when this category applies. Shown to whoever files a request."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={error?.fieldError('description')}
            disabled={saving}
          />
        </div>

        <FormFieldset legend="Document checklist">
          <InlineAddTable
            headers={['Document *', 'Reviewer *', 'Description']}
            colWidths={[240, 220, 300]}
            resizeKey={`${ID_PREFIX}-documents-v1`}
            flexColumn={2}
            addButtonId={`${ID_PREFIX}-add-document`}
            addDisabled={saving || lines.some((l) => !isFilled(l))}
            addDisabledReason="Finish the current document before adding another."
            rowCount={lines.length}
            onAddRow={() => setLines((current) => [...current, EMPTY()])}
          >
            {lines.map((line, index) => (
              <InlineRow
                key={line.key}
                idx={index + 1}
                onRemove={() => setLines((current) => current.filter((l) => l.key !== line.key))}
              >
                <TdInput
                  id={cellId(index, 'name')}
                  ariaLabel={`Document, line ${index + 1}`}
                  value={line.name}
                  onChange={(next) => patch(line.key, { name: next })}
                  placeholder="Clearance"
                  disabled={saving}
                />
                <TdSelect
                  id={cellId(index, 'reviewerUserId')}
                  ariaLabel={`Reviewer, line ${index + 1}`}
                  value={line.reviewerUserId}
                  onChange={(next) => patch(line.key, { reviewerUserId: next || null })}
                  options={reviewerOptions}
                  remote
                  disabled={saving || loadingUsers}
                />
                <TdInput
                  id={cellId(index, 'description')}
                  ariaLabel={`Description, line ${index + 1}`}
                  value={line.description}
                  onChange={(next) => patch(line.key, { description: next })}
                  disabled={saving}
                />
              </InlineRow>
            ))}
          </InlineAddTable>

          {error?.fieldError('documents') ? (
            <p className="text-xs text-red-700">{error.fieldError('documents')}</p>
          ) : null}
        </FormFieldset>

        {/*
          The CONSEQUENCE, on the form where it is read before the tick: the checklist is copied
          onto a request when it is filed, and the reviewer is read live.
        */}
        <p className="text-xs text-slate-500">
          A request filed under this category asks for every document on the checklist as it
          stands when the request is filed. Changing a reviewer moves the documents already
          waiting on them; removing a document leaves it on the requests already filed.
        </p>
      </div>
    </Modal>
  );
}
